import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconEdit, IconTrash } from '../components/icons';
import { labelFor, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { formatDateTime } from '../utils/format';

// TS port of the source (Falah) app's pages/FacilityDetail.jsx.
interface FacilityRecord {
  id: number;
  name: string;
  building: string | null;
  floor: string | null;
  capacity: number | null;
  description: string | null;
  maintenance_notes: string | null;
  status: string;
  photo_url: string | null;
}

interface BookingRow {
  id: number;
  title: string;
  booked_by: string | null;
  start_at: string;
  end_at: string;
}

export function FacilityDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { lists } = useConfig();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [facility, setFacility] = useState<FacilityRecord | null>(null);
  const [bookings, setBookings] = useState<BookingRow[] | null>(null);

  useEffect(() => {
    api.get<FacilityRecord>(`/facilities/${id}`).then((res) => setFacility(res.data));
  }, [id]);

  useEffect(() => {
    api
      .get<{ data: BookingRow[] }>('/bookings', {
        params: { facility_id: id, status: 'confirmed', from: new Date().toISOString().slice(0, 10), per_page: 5 },
      })
      .then((res) => setBookings(res.data.data));
  }, [id]);

  const remove = async () => {
    if (!facility) return;
    if (!(await confirmDelete(`Delete "${facility.name}"?`))) return;
    try {
      await api.delete(`/facilities/${id}`);
      navigate('/resources/facilities');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  if (!facility) return null;

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/resources/facilities')} />
          <IconButton icon={IconEdit} label="Edit" to={`/resources/facilities/${id}/edit`} />
          <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={remove} />
        </div>
      </header>

      <div className="card">
        <div className="detail-summary">
          <div>
            <div className="detail-summary-title">{facility.name}</div>
            <span className={`badge badge-${facility.status === 'active' ? 'normal' : 'important'}`}>
              {labelFor(lists, 'facility_status', facility.status)}
            </span>
            <div className="detail-summary-service">
              {[facility.building, facility.floor].filter(Boolean).join(' · ') || 'No location set'}
            </div>
          </div>
        </div>

        {facility.photo_url && (
          <div className="detail-item detail-item-full">
            <img
              src={facility.photo_url}
              alt={facility.name}
              style={{ maxWidth: '320px', borderRadius: '8px', display: 'block' }}
            />
          </div>
        )}

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-item-label">Capacity</span>
            <span className={`detail-item-value${facility.capacity ? '' : ' empty'}`}>
              {facility.capacity || 'Not set'}
            </span>
          </div>
        </div>

        <div className="detail-item detail-item-full">
          <span className="detail-item-label">Description</span>
          {facility.description ? (
            <p className="detail-notes">{facility.description}</p>
          ) : (
            <span className="detail-item-value empty">Not provided</span>
          )}
        </div>

        <div className="detail-item detail-item-full">
          <span className="detail-item-label">Maintenance notes</span>
          {facility.maintenance_notes ? (
            <p className="detail-notes">{facility.maintenance_notes}</p>
          ) : (
            <span className="detail-item-value empty">Not provided</span>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Upcoming bookings</h2>
          <Link className="btn btn-ghost" to={`/resources/bookings/new?facility_id=${id}`}>
            + Add booking
          </Link>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Booked By</th>
                <th>Start</th>
                <th>End</th>
              </tr>
            </thead>
            <tbody>
              {bookings?.map((b) => (
                <tr key={b.id}>
                  <td>
                    <Link to={`/resources/bookings/${b.id}`}>{b.title}</Link>
                  </td>
                  <td className="muted">{b.booked_by || '—'}</td>
                  <td>{formatDateTime(b.start_at)}</td>
                  <td>{formatDateTime(b.end_at)}</td>
                </tr>
              ))}
              {bookings?.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted center">
                    No upcoming bookings.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
